import { createCloseHandlerStack, provideActivityOrchestrator } from '~/composables/unified/useActivityOrchestrator';
import { useOneLeaving } from '~/composables/unified/useBackButtonPlace';
import { EXPERIENCE_FILTER_KEYS } from '~/composables/unified/useExperienceFilters';
import { useIaApplies } from '~/composables/unified/useIaAudience';
import { useGhostStudent } from '~/composables/useGhostStudent';
import { COACH_ROUTE_INDEX, COACH_ROUTE_PROFILE } from '~/constants/routes/coach';
import { UNIFIED_ROUTE_CAREERS, UNIFIED_ROUTE_EXPLORE, UNIFIED_ROUTE_HUB_DETAIL, UNIFIED_ROUTE_INDEX, UNIFIED_ROUTE_LEARN, UNIFIED_ROUTE_LEARN_EXPLORE, UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import { PUBLIC_BACK_PLACE_ROUTES, publicBackPlace, publicHomePlace } from '~/utils/unified/publicBackPlace';

// Consideration-area slugs are lowercase kebab-case; anything else in `?hub=` is not one of ours.
const HUB_SLUG = /^[a-z0-9-]+$/;

// The IA Learn page's view state that a card carries onto the activity and back.
const LEARN_RETURN_PARAMS = ['category', 'keyword', ...EXPERIENCE_FILTER_KEYS];

/**
 * `onExit` tears the player down instead of navigating away from it.
 *
 * An anonymous lesson renders in place on `/start`, so it has no route of its own: the ordinary
 * exits navigate, which is a no-op here because the student is already on the page they would
 * navigate to. Only the page that mounted the player can unmount it, so it says how.
 */
export interface PublicActivityPageOptions {
  onExit?: () => void;
  /** The page is hosting the anonymous lesson itself, so its participant is the right user. */
  hostsAnonymousLesson?: boolean;
}

export const usePublicActivityPage = (options: PublicActivityPageOptions = {}) => {
  const { $logErrorMessage, $analytics } = useNuxtApp();
  const route = useRoute();
  const { isLoggedIn, isGhostStudent, isLessonGhost, createGhostStudent } = useGhostStudent();
  const configStore = useConfigStore();
  const iaApplies = useIaApplies();
  const router = useRouter();
  const leaveOnce = useOneLeaving();
  const ghostCreating = ref(false);

  const ensureGhostUser = async (triggerAction = 'content_start') => {
    // The page hosting the anonymous lesson never mints from here, whatever the session state:
    // its one mint is the join card's Start press, which the server answers with a lesson participant.
    // Without this, a session that lapses mid-lesson reads as logged out and the fallback below
    // would hand it a tracked consumer ghost inside the lesson that promised no identity.
    if (options.hostsAnonymousLesson) {
      return;
    }
    // An anonymous lesson participant counts as logged in but must not absorb consumer work: its
    // row is meant to be purged with the lesson and never merges, so anything done on it here is work the
    // person loses. The mint replaces it with an ordinary consumer ghost. Consumer clusters only:
    // a schools cluster answers this mint with another participant, so the press would strand an
    // orphan row and change nothing.
    const participantOnConsumerContent = isLessonGhost.value && !configStore.isSchoolsCluster;
    if ((isLoggedIn.value && !participantOnConsumerContent) || ghostCreating.value) {
      return;
    }
    ghostCreating.value = true;
    const ok = await createGhostStudent();
    ghostCreating.value = false;
    if (!ok) {
      $logErrorMessage('Failed to create ghost student for public flow');
    }
    else {
      $analytics.track(EVENTS.UNIFIED_PUBLIC_GHOST_STUDENT_CREATED, {
        triggerPage: String(route.name ?? 'unknown'),
        triggerAction,
      });
    }
  };

  const closeHandlers = createCloseHandlerStack();

  // The Considerations Profile is a route of the coach shell, so returning to it is a navigation
  // like any other.
  const considerationsProfileDestination = () => COACH_ROUTE_PROFILE;

  const exitDestination = () => {
    // `?from=` is set by the page that opened the card so close returns the user there.
    if (route.query.from === 'explore') {
      // Restore the Explore search the user came from, if any, so their results aren't lost.
      const keyword = typeof route.query.keyword === 'string' ? route.query.keyword : undefined;
      return { path: UNIFIED_ROUTE_LEARN_EXPLORE, query: keyword ? { keyword } : {} };
    }
    if (route.query.from === 'explore-index') {
      return UNIFIED_ROUTE_EXPLORE;
    }
    if (route.query.from === 'learn') {
      // Back to the Learn category the card was in (and its search and filters), not just the page.
      const carried = Object.fromEntries(
        LEARN_RETURN_PARAMS.flatMap(key => (typeof route.query[key] === 'string' ? [[key, route.query[key]]] : [])),
      );
      return Object.keys(carried).length ? { path: UNIFIED_ROUTE_LEARN, query: carried } : UNIFIED_ROUTE_LEARN;
    }
    if (route.query.from === 'career-advice') {
      return UNIFIED_ROUTE_CAREERS;
    }
    if (route.query.from === 'profile') {
      return considerationsProfileDestination();
    }
    // An industry hub names itself so its learn-more cards close back onto the hub.
    if (route.query.from === 'hub' && typeof route.query.hub === 'string' && HUB_SLUG.test(route.query.hub)) {
      return UNIFIED_ROUTE_HUB_DETAIL(route.query.hub);
    }
    return UNIFIED_ROUTE_INDEX;
  };

  provideActivityOrchestrator({
    navigateToActivity: () => {},
    completeFlow: () => {
      if (options.onExit) {
        options.onExit();
        return;
      }

      if (isLoggedIn.value && !isGhostStudent.value) {
        // A self-discovery story launched from the profile returns there on completion,
        // rather than dropping the user on the bare chat.
        if (route.query.from === 'profile') {
          return navigateTo(considerationsProfileDestination());
        }
        return navigateTo(COACH_ROUTE_INDEX);
      }
      return navigateTo(UNIFIED_ROUTE_LOGIN);
    },
    cancelFlow: () => {
      if (options.onExit) {
        options.onExit();
        return;
      }

      // Under the IA the close goes where the overview's way back does, as the coach shell's does: back
      // to the page of ours behind it, which already holds that page's filters and scroll, else to the
      // page's place. The query's named origin is for the pages without that way back.
      if (iaApplies.value) {
        if (publicBackPlace(window.history.state?.back) !== undefined) {
          return leaveOnce(() => router.back());
        }
        return navigateTo(PUBLIC_BACK_PLACE_ROUTES[publicHomePlace(route.path)]);
      }

      return navigateTo(exitDestination());
    },
    navigateAway: async (to: string) => { await navigateTo(to); },
    notifyFlowStarted: () => ensureGhostUser(),
    pushCloseHandler: closeHandlers.push,
    popCloseHandler: closeHandlers.pop,
  });
};
